import React from 'react';

/**
 * A reusable purple button component with consistent styling
 * 
 * @param {Object} props - Component props
 * @param {string} props.className - Additional classes to apply
 * @param {React.ReactNode} props.children - Button content
 * @param {boolean} props.withIcon - Whether to include the plus icon
 * @param {function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string|React.ComponentType} props.as - Element or component to render as
 * @param {Object} props.rest - Other props to pass to the element
 */
const PurpleButton = ({ 
  className = '', 
  children, 
  withIcon = false,
  onClick, 
  type = 'button',
  disabled = false,
  as: Component = 'button',
  ...rest 
}) => {
  const baseClassName = `inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/30 px-4 py-2.5 text-sm font-semibold text-purple-600 dark:text-purple-300 shadow-sm ring-1 ring-inset ring-purple-200 dark:ring-purple-600/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`;
  
  const buttonProps = Component === 'button' ? { type } : {};
  
  return (
    <Component
      {...buttonProps}
      onClick={onClick}
      disabled={disabled}
      className={baseClassName}
      {...rest}
    >
      {withIcon && (
        <svg className="-ml-0.5 mr-1.5 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
        </svg>
      )}
      {children}
    </Component>
  );
};

export default PurpleButton;
